import type { VariantProps } from 'class-variance-authority'
import type { badgeVariants } from '@/components/ui/badge'
import type { TicketStatus, TicketPriority } from '@/types/ticket'
import type { TrackerStatus, DevStage } from '@/types/tracker'

type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>['variant']>

export const statusLabels: Record<TicketStatus, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  pending: 'Awaiting You',
  resolved: 'Resolved',
  closed: 'Closed',
}

export const priorityLabels: Record<TicketPriority, string> = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
}

export const statusVariants: Record<TicketStatus, string> = {
  open: 'default',
  in_progress: 'secondary',
  pending: 'outline',
  resolved: 'secondary',
  closed: 'outline',
}

const ADMIN_TRANSITIONS: Record<TicketStatus, TicketStatus[]> = {
  open: ['in_progress', 'pending', 'resolved'],
  in_progress: ['pending', 'resolved', 'open'],
  pending: ['in_progress', 'resolved', 'open'],
  resolved: ['closed', 'open'],
  closed: ['open'],
}

export function nextStatuses(current: TicketStatus): TicketStatus[] {
  return ADMIN_TRANSITIONS[current] ?? []
}

export const trackerStatusLabels: Record<TrackerStatus, string> = {
  new: 'New',
  needs_info: 'Needs info',
  accepted: 'Accepted',
  proposed_closed: 'Proposed closed',
  closed: 'Closed',
  on_hold: 'On hold',
  wont_do: "Won't do",
}

export const trackerStatusVariants: Record<TrackerStatus, BadgeVariant> = {
  new: 'default',
  needs_info: 'outline',
  accepted: 'secondary',
  proposed_closed: 'outline',
  closed: 'outline',
  on_hold: 'outline',
  wont_do: 'destructive',
}

export const devStageLabels: Record<DevStage, string> = {
  not_scoped: 'Not scoped',
  scoped: 'Scoped',
  planned: 'Planned',
  in_progress: 'In progress',
  shipped: 'Shipped',
  cancelled: 'Cancelled',
}

export const devStageVariants: Record<DevStage, BadgeVariant> = {
  not_scoped: 'outline',
  scoped: 'outline',
  planned: 'secondary',
  in_progress: 'default',
  shipped: 'secondary',
  cancelled: 'destructive',
}

export const trackerStatusOrder: TrackerStatus[] = [
  'new',
  'needs_info',
  'accepted',
  'proposed_closed',
  'closed',
  'on_hold',
  'wont_do',
]
